import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { HomeFeedResponse } from './useHomeFeed';

interface UpdateOrderDetailsInput {
    orderId?: string;
    orderIds?: string[];
    roNumber?: string;
    locationName?: string;
    potentialTotal?: boolean;
    manuallyFlagged?: boolean;
    quotesOnly?: boolean;
    note?: string;
    noteType?: 'note' | 'message';
    mentionedUserIds?: string[];
    explicitMentionIds?: string[];
    editNote?: {
        _id: string;
        newText: string;
    };
    deleteNoteId?: string;
    allowAutoOrder?: boolean;
    reviewedByMe?: boolean;
}

const updateOrderDetails = async (data: UpdateOrderDetailsInput, sessionId: string | null) => {
    return axios.post(`${API_URL}/order/update`,
        data,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
};

export const useUpdateOrderDetails = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: UpdateOrderDetailsInput) => updateOrderDetails(data, sessionContext?.sessionId ?? null),
        onMutate: variables => {
            if (variables.orderIds?.length || !variables.orderId) return;

            const applyLocalUpdate = (oldData: any) => {
                if (!oldData) return oldData;

                let reviewedByUsers = oldData.reviewedByUsers;
                if (variables.reviewedByMe !== undefined && Array.isArray(oldData.reviewedByUsers) && sessionContext?.isAuthenticated) {
                    const existing = oldData.reviewedByUsers as Array<{ _id: string; firstName: string; lastName: string; displayName: string; }>;
                    const me = {
                        _id: sessionContext.userId,
                        firstName: sessionContext.firstName,
                        lastName: sessionContext.lastName,
                        displayName: sessionContext.firstName,
                    };

                    reviewedByUsers = variables.reviewedByMe
                        ? (existing.some((reviewer) => reviewer._id === me._id) ? existing : [...existing, me])
                        : existing.filter((reviewer) => reviewer._id !== me._id);
                }

                return {
                    ...oldData,
                    ...Object.fromEntries(
                        Object.entries(variables).filter(([key]) => key !== 'orderId' && key !== 'reviewedByMe')
                    ),
                    reviewedByUsers
                };
            };

            // Keep both order-detail and drawer caches in sync for tag rendering.
            queryClient.setQueryData(['/order/detail', variables.orderId], applyLocalUpdate);
            queryClient.setQueryData(['/order/drawer', variables.orderId], applyLocalUpdate);
            queryClient.setQueryData(['quoteData', variables.orderId], (oldData: { roNumber?: string; locationName?: string; } | undefined) => {
                if (!oldData) return oldData;
                return {
                    ...oldData,
                    ...(variables.roNumber !== undefined ? { roNumber: variables.roNumber } : {}),
                    ...(variables.locationName !== undefined ? { locationName: variables.locationName } : {}),
                };
            });
            queryClient.setQueryData(['order-confirmation', variables.orderId], (oldData: { roNumber?: string; } | undefined) => {
                if (!oldData) return oldData;
                return {
                    ...oldData,
                    ...(variables.roNumber !== undefined ? { roNumber: variables.roNumber } : {}),
                };
            });

            if (variables.reviewedByMe !== undefined) {
                queryClient.setQueriesData<HomeFeedResponse>({ queryKey: ['/orders/home-feed-paginated'] }, (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        orders: old.orders.map((order) => {
                            if (order._id !== variables.orderId) return order;

                            const currentlyReviewedByMe = order.reviewedByCurrentUser;
                            const currentCount = order.reviewedByCount ?? 0;
                            let nextCount = currentCount;

                            if (variables.reviewedByMe === true && !currentlyReviewedByMe) {
                                nextCount = currentCount + 1;
                            } else if (variables.reviewedByMe === false && currentlyReviewedByMe) {
                                nextCount = Math.max(currentCount - 1, 0);
                            }

                            return {
                                ...order,
                                reviewedByCurrentUser: variables.reviewedByMe as boolean,
                                reviewedByCount: nextCount
                            };
                        })
                    };
                });
            }
        }
    });
};
